import { Card, Dropdown, makeStyles, mergeClasses, Option, tokens } from '@fluentui/react-components';
import { automationRuleTriggerOptions, triggerTypes, useAutomationRuleTrigger } from './use-trigger';
import { useCallback, useMemo } from 'react';
import WorkItemTransitionsForm from './work-item-transitions';
import FieldValueChangedForm from './field-value-changed-form';
import AssignedToForm from './assigned-to-form';
import { ChecklistTriggerType, FieldValueChangedTriggerType, TriggerItemType, WorkItemIsAssignedToTriggerType, WorkItemTransitionTriggerType } from './types';
import useFieldValueChanged, { initialFieldValueChange } from './field-value-changed-form/use-field-value-changed';
import { useAzureProvider } from 'src/azure/context';
import useAssignedToForm, { inititalAssignedToForm } from './assigned-to-form/use-assigned-to-form';
import useWorkItemTransition, { initialWorkItemTransitions } from './work-item-transitions/use-work-item-transitions';
import CheckListTrigger from './checklist';

export default function AutomationRuleTrigger() {
	const classes = useStyles();
	const {applicationContext} = useAzureProvider();
	const {trigger, triggerData, setTriggerData,  setTrigger, validationErrors, resetValidations} = useAutomationRuleTrigger();
	const fieldValueChangedHook = useFieldValueChanged();
	const isAssignedToHook = useAssignedToForm();
	const wiTransitions = useWorkItemTransition();

	const subForm = useMemo(() => {
		if (trigger === triggerTypes.fieldValueIsChanged && triggerData) {
			return (
				<FieldValueChangedForm
					validations={validationErrors.fieldValueChanged || null}
					data={triggerData?.fieldValueChange as FieldValueChangedTriggerType}
				/>
			);
		}

		if (trigger === triggerTypes.workItemTransitions && triggerData) {
			return <WorkItemTransitionsForm
				data={triggerData?.workItemTransitions as WorkItemTransitionTriggerType}
				validations={validationErrors.workItemTransitions || null}
			/>;
		}

		if (trigger === triggerTypes.workItemIsAssignedTo && triggerData) {
			return (
				<AssignedToForm
					validations={validationErrors.workItemIsAssignedTo || null}
					data={triggerData?.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType}
				/>
			);
		}

		if ((trigger === triggerTypes.checkListIsCompleted || trigger === triggerTypes.checkListIsNotCompleted) && triggerData) {
			return (
				<CheckListTrigger
					validations={validationErrors.checklistTrigger || null}
					data={triggerData?.checklistTrigger as ChecklistTriggerType}
				/>
			);
		}

		return null;
	}, [
		trigger,
		triggerData?.fieldValueChange,
		triggerData?.workItemTransitions,
		triggerData?.workItemTransitions,
		triggerData?.workItemIsAssignedTo,
		triggerData?.checklistTrigger,
		validationErrors.trigger,
		validationErrors.fieldValueChanged.fieldName,
		validationErrors.workItemIsAssignedTo.identity,
		validationErrors.workItemTransitions.from,
		validationErrors.workItemTransitions.to,
		validationErrors.checklistTrigger.field,
	]);

	const changeTriggerType = useCallback(async (value: string) => {
		resetValidations();
		setTrigger(value);
	
		if (value === triggerTypes.workItemTransitions) {
			setTriggerData((oldData) => {
				const data = oldData as TriggerItemType;
				return {...data, workItemTransitions: {...initialWorkItemTransitions}};
			});

			const transitionOptions = await wiTransitions.getTransitionOptions({
				organizationName: applicationContext.organizationName,
				projectId: applicationContext.projectId
			});

			setTriggerData((oldData) => {
				const data = oldData as TriggerItemType;
				return {
					...data,
					workItemTransitions: {
						...initialWorkItemTransitions,
						loading: false,
						transitionOptions: transitionOptions
					}
				};
			});
		}

		if (value === triggerTypes.workItemIsAssignedTo) {
			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				return {
					...data,
					workItemIsAssignedTo: {
						...inititalAssignedToForm,
					}
				};
			});

			const identityOptions = await isAssignedToHook.getIdentityListOptions();

			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				const isAssignedTovalues = data.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType;
				return {
					...data,
					workItemIsAssignedTo: {
						...isAssignedTovalues,
						loading: false,
						identityOptions: identityOptions
					}
				};
			});
		}

		if (value === triggerTypes.fieldValueIsChanged) {
			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				return {...data, fieldValueChange: initialFieldValueChange};
			});

			const fieldOptions = await fieldValueChangedHook.getValueOptions({
				projectId: applicationContext.projectId,
			});

			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				const values = data.fieldValueChange as FieldValueChangedTriggerType;
				return {...data, fieldValueChange: {
					...values,
					loading: false,
					fieldOptions: fieldOptions
				}};
			});
		}

		if (value === triggerTypes.checkListIsCompleted || value === triggerTypes.checkListIsNotCompleted) {
			setTriggerData((oldData) => {
				return {...oldData as TriggerItemType, checklistTrigger: { field: '' }};
			});
		}

		// is assigned to
	}, [applicationContext]);
	
	return (
		<Card>
			<div className={classes.dropdownRoot}>
				<Dropdown
					className={mergeClasses(
						classes.dropdown,
						classes.triggerTypeDropdown,
						validationErrors.trigger ? classes.dropdownWithError : ''
					)}
					listbox={{
						style: {
							minWidth: '224px'
						}
					}}
					appearance='underline'
					placeholder='When'
					positioning="below-start"
					value={automationRuleTriggerOptions.find(triggerItem => trigger === triggerItem.value)?.label || ''}
					onOptionSelect={async (_, data) => {
						changeTriggerType(data.optionValue as string);
					}}
				>
					{automationRuleTriggerOptions.map((triggerItem) => (
						<Option key={triggerItem.value} value={triggerItem.value}>
							{triggerItem.label}
						</Option>
					))}
				</Dropdown>
			</div>
			{
				validationErrors.trigger && (
					<span className={classes.helperTextForError}>
						{validationErrors.trigger}
					</span>
				)
			}
			{subForm}
		</Card>
	);
}

const useStyles = makeStyles({
	root: {

	},
	dropdownRoot: {
		width: '100%'
	},
	helperTextForError: {
		width: '100%',
		color: tokens.colorPaletteRedBorder2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
		padding: '0px 4px'
	},
	dropdown: {
		minWidth: 'auto',
		// width: 'max-content',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none',
		},
		':hover': {
			background: tokens.colorNeutralShadowAmbientLighter,
		},
	},
	dropdownWithError: {
		backgroundColor: tokens.colorPaletteRedBackground2,
		'& button': {
			color: tokens.colorPaletteRedBorder2,
		},
		'& svg': {
			color: tokens.colorPaletteRedBorder2,
		},
		':hover': {
			backgroundColor: tokens.colorPaletteRedBackground2,
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
		}
	},
	triggerTypeDropdown: {
		// maxWidth: '240px',
	},
	subForm: {
		width: '100%',
		display: 'flex',
	},
	subFormDropdown: {
		flex: '1 1 0px',
		minWidth: '120px',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		}
	}
});